"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const ice_1 = require("ice");
// maps constructor functions to type paths (Module.Type) for structs and enums
const typeMap = new Map();
function isStructConstructor(constructor) {
    return constructor.prototype.equals === ice_1.Ice.Identity.prototype.equals;
}
function isEnumConstructor(constructor) {
    return (constructor.prototype.equals === ice_1.Ice.EnumBase.prototype.equals &&
        constructor !== ice_1.Ice.EnumBase);
}
function cacheConstructors(iceModule, prefix) {
    if (iceModule == null) {
        // hack to get an object containing all top-level Slice modules
        iceModule = ice_1.Ice._ModuleRegistry.type({ split: () => [] });
    }
    for (const name of Object.keys(iceModule)) {
        const value = iceModule[name];
        if (value.constructor === Object) {
            // value is a module
            cacheConstructors(value, prefix == null ? name : `${prefix}.${name}`);
        }
        else if (typeof value === 'function') {
            if (isStructConstructor(value) || isEnumConstructor(value)) {
                typeMap.set(value, prefix == null ? name : `${prefix}.${name}`);
            }
        }
    }
}
cacheConstructors();
function getType(enumOrStruct) {
    let type = typeMap.get(enumOrStruct.constructor);
    if (type == null) {
        cacheConstructors();
        type = typeMap.get(enumOrStruct.constructor);
    }
    if (type == null) {
        throw new Error(`Could not find type for ${JSON.stringify(enumOrStruct)}`);
    }
    return type;
}
function getConstructor(type) {
    const constructor = ice_1.Ice._ModuleRegistry.type(type);
    if (constructor == null) {
        throw new Error(`Could not find constructor for type ${type}`);
    }
    return constructor;
}
exports.ICE_TYPE_KEY = '@ice-type';
exports.LONG_TYPE = 'Ice.Long';
function longToPlain(long) {
    // serialize to number if possible
    const value = long.toNumber();
    if (value !== Number.POSITIVE_INFINITY &&
        value !== Number.NEGATIVE_INFINITY) {
        return {
            [exports.ICE_TYPE_KEY]: exports.LONG_TYPE,
            value,
        };
    }
    else {
        return {
            [exports.ICE_TYPE_KEY]: exports.LONG_TYPE,
            high: long.high,
            low: long.low,
        };
    }
}
function longFromPlain(plainLong) {
    if (plainLong.value != null) {
        return new ice_1.Ice.Long(plainLong.value);
    }
    else {
        return new ice_1.Ice.Long(plainLong.high, plainLong.low);
    }
}
function enumToPlain(enumValue) {
    return {
        [exports.ICE_TYPE_KEY]: getType(enumValue),
        value: enumValue.name,
    };
}
function enumFromPlain(constructor, plainEnum) {
    return constructor[plainEnum.value];
}
exports.MAP_TYPE = 'Map';
function mapToPlain(map, recursive = true) {
    const entries = {};
    const ret = {
        [exports.ICE_TYPE_KEY]: exports.MAP_TYPE,
        entries,
    };
    for (const [key, value] of map.entries()) {
        entries[JSON.stringify(key)] = recursive ? iceToPlain(value) : value;
    }
    return ret;
}
function mapFromPlain(plainMap, recursive = true) {
    const { entries } = plainMap;
    const ret = new Map();
    for (const key of Object.keys(entries)) {
        ret.set(JSON.parse(key), recursive ? iceFromPlain(entries[key]) : entries[key]);
    }
    return ret;
}
exports.HASH_MAP_TYPE = 'Ice.HashMap';
function hashMapToPlain(hashMap, recursive = true) {
    const entries = [];
    const ret = {
        [exports.ICE_TYPE_KEY]: exports.HASH_MAP_TYPE,
        entries,
    };
    for (const [key, value] of hashMap.entries()) {
        entries.push({
            key: recursive ? iceToPlain(key) : key,
            value: recursive ? iceToPlain(value) : value,
        });
    }
    return ret;
}
function hashMapFromPlain(plainHashMap, recursive = true) {
    const { entries } = plainHashMap;
    const ret = new ice_1.Ice.HashMap();
    for (const { key, value } of entries) {
        ret.set(recursive ? iceFromPlain(key) : key, recursive ? iceFromPlain(value) : value);
    }
    return ret;
}
function structToPlain(struct, recursive = true) {
    const ret = {
        [exports.ICE_TYPE_KEY]: getType(struct),
    };
    for (const key of Object.keys(struct)) {
        ret[key] = recursive ? iceToPlain(struct[key]) : struct[key];
    }
    return ret;
}
function objectFromPlain(constructor, plainObject, recursive = true) {
    const ret = new constructor();
    for (const key of Object.keys(plainObject)) {
        if (key === exports.ICE_TYPE_KEY) {
            continue;
        }
        ret[key] = recursive ? iceFromPlain(plainObject[key]) : plainObject[key];
    }
    return ret;
}
function objectToPlain(value, recursive = true) {
    const ret = {
        [exports.ICE_TYPE_KEY]: value
            .ice_id()
            .replace(/::/g, '.')
            .slice(1),
    };
    for (const key of Object.keys(value)) {
        if (key.startsWith('_')) {
            continue;
        }
        ret[key] = recursive ? iceToPlain(value[key]) : value[key];
    }
    return ret;
}
function mapValues(obj, fn) {
    const ret = {};
    for (const key of Object.keys(obj)) {
        ret[key] = fn(obj[key]);
    }
    return ret;
}
function iceToPlain(iceValue, recursive = true) {
    if (iceValue === null || typeof iceValue !== 'object') {
        return iceValue;
    }
    if (iceValue instanceof ice_1.Ice.Long) {
        return longToPlain(iceValue);
    }
    if (iceValue instanceof ice_1.Ice.EnumBase) {
        return enumToPlain(iceValue);
    }
    if (Array.isArray(iceValue)) {
        return recursive
            ? iceValue.map(value => iceToPlain(value, true))
            : iceValue;
    }
    if (iceValue instanceof Map) {
        return mapToPlain(iceValue, recursive);
    }
    if (iceValue instanceof ice_1.Ice.HashMap) {
        return hashMapToPlain(iceValue, recursive);
    }
    if (isStructConstructor(iceValue.constructor)) {
        return structToPlain(iceValue, recursive);
    }
    if (iceValue instanceof ice_1.Ice.Value || iceValue instanceof ice_1.Ice.Exception) {
        return objectToPlain(iceValue, recursive);
    }
    return recursive
        ? mapValues(iceValue, value => iceToPlain(value, true))
        : iceValue;
}
exports.iceToPlain = iceToPlain;
function iceFromPlain(plainValue, recursive = true) {
    if (plainValue === null || typeof plainValue !== 'object') {
        return plainValue;
    }
    if (Array.isArray(plainValue)) {
        return recursive
            ? plainValue.map(value => iceFromPlain(value, true))
            : plainValue;
    }
    const iceType = plainValue[exports.ICE_TYPE_KEY];
    if (iceType == null) {
        return recursive
            ? mapValues(plainValue, value => iceFromPlain(value, true))
            : plainValue;
    }
    if (iceType === exports.LONG_TYPE) {
        return longFromPlain(plainValue);
    }
    if (iceType === exports.MAP_TYPE) {
        return mapFromPlain(plainValue, recursive);
    }
    if (iceType === exports.HASH_MAP_TYPE) {
        return hashMapFromPlain(plainValue, recursive);
    }
    const constructor = getConstructor(iceType);
    if (isEnumConstructor(constructor)) {
        return enumFromPlain(constructor, plainValue);
    }
    return objectFromPlain(constructor, plainValue, recursive);
}
exports.iceFromPlain = iceFromPlain;
//# sourceMappingURL=index.js.map


//////////////////
// WEBPACK FOOTER
// ./~/ice-to-plain/lib/index.js
// module id = 749
// module chunks = 0